<script setup lang="ts">
defineProps<{
  image?: string
  category?: string
  title?: string
  excerpt?: string
  slug?: string
}>()

const sectionRef = ref<HTMLElement | null>(null)
const isVisible = ref(false)

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        isVisible.value = true
        observer.disconnect()
      }
    },
    { threshold: 0.15 },
  )
  if (sectionRef.value) observer.observe(sectionRef.value)
  onUnmounted(() => observer.disconnect())
})
</script>

<template>
  <section
    ref="sectionRef"
    class="max-w-7xl mx-auto px-4 py-16 md:px-20 md:py-10 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-18 items-center"
  >
    <!-- Image column -->
    <div
      :class="[
        'featured-image-col relative overflow-hidden transition-all duration-700 ease-out md:order-1',
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 md:translate-x-8',
      ]"
    >
      <img
        v-if="image"
        :src="image"
        :alt="title ?? ''"
        class="block w-full aspect-[4/3] object-cover transition-transform duration-500 hover:scale-[1.04]"
      />
      <div v-else class="w-full aspect-[4/3] bg-raised" />
    </div>

    <!-- Text column -->
    <div
      :class="[
        'flex flex-col gap-6 md:order-0 transition-all duration-700 ease-out',
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8 md:-translate-x-8',
      ]"
    >
      <p
        v-if="category"
        :class="[
          'featured-eyebrow font-label font-light text-xs leading-4 tracking-wider text-brand',
          isVisible && 'featured-eyebrow--visible',
        ]"
      >
        {{ category }}
      </p>

      <h2 class="font-serif font-normal text-3xl md:text-5xl leading-tight text-navy">
        {{ title }}
      </h2>

      <p
        v-if="excerpt"
        class="font-sans font-light text-sm md:text-base leading-6 text-secondary max-w-lg"
      >
        {{ excerpt }}
      </p>

      <NuxtLink v-if="slug" :to="slug" class="self-start">
        <AppButton type="Ghost" size="Small" label="Read the Story" :showIconRight="true" />
      </NuxtLink>
    </div>
  </section>
</template>

<style scoped>
/* Bottom border accent on image hover */
.featured-image-col::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 0;
  background: var(--color-text-brand);
  transition: width 300ms ease-out;
}

.featured-image-col:hover::after {
  width: 100%;
}

/* Eyebrow clip-path reveal */
.featured-eyebrow {
  clip-path: inset(0 100% 0 0);
  transition: clip-path 500ms ease-out 100ms;
}

.featured-eyebrow--visible {
  clip-path: inset(0 0% 0 0);
}

/* Mobile: simplified entrance — both columns fade up instead of slide horizontally */
@media (max-width: 768px) {
  .featured-image-col,
  .flex.flex-col {
    transform: translateY(20px) !important;
  }

  .featured-image-col.opacity-100,
  .flex.flex-col.opacity-100 {
    transform: translateY(0) !important;
  }
}
</style>
